import type { AxiosRequestConfig } from 'axios';

import type { LiceSource } from '@/hooks/useLiceSource';
import type { SwaggerResponse } from '@/services/config';
import { Http } from '@/services/httpRequest';
import type { AllWeeklyReportResult, LiceParityDto } from '@/services/types';

/**
 * Hand-written fetchers for the week-scoped lice report routes.
 *
 * `services/` is generated and was not regenerated: the local API was not running, so
 * `dump:api-swagger` could not refresh the spec. Replace these with the generated client once
 * that pipeline has run (`yarn workspace optoscale-backend dump:api-swagger`, then
 * `yarn workspace optoscale-frontend updateApi`). The expected names are
 * `ClientController_LiceWeeklyReportWeeks` / `useClientController_LiceWeeklyReportWeeks` and
 * `ClientController_LiceWeeklyReport` / `useClientController_LiceWeeklyReport`.
 *
 * UI code must keep importing the domain hooks, not these functions and not `@/services`.
 */
const WEEKS_URL = (clientId: number): string =>
    `/api/v2/client/internal/${clientId}/lice/weeklyReport/weeks`;

const WEEK_URL = (clientId: number): string =>
    `/api/v2/client/internal/${clientId}/lice/weeklyReport`;

export interface LiceReportWeekDto {
    isoWeek: number;
    isoYear: number;
    interimReport: boolean;
    /** Locations in a stored week. Empty for the running week until it is stored. */
    locationIds: number[];
}

export interface LiceReportWeeksResponseDto {
    weeks: LiceReportWeekDto[];
}

export interface WeeklyLiceReportResponseDto {
    report: AllWeeklyReportResult;
    source: LiceSource;
    parity?: LiceParityDto;
}

export interface WeeklyLiceReportQuery {
    isoWeek: number;
    isoYear: number;
    locationId?: number;
    source: LiceSource;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function isLiceSource(value: unknown): value is LiceSource {
    return value === 'sql' || value === 'core' || value === 'compare';
}

function isNumberList(value: unknown): value is number[] {
    return Array.isArray(value) && value.every((item) => typeof item === 'number');
}

function isLiceReportWeek(value: unknown): value is LiceReportWeekDto {
    return (
        isRecord(value) &&
        typeof value.isoWeek === 'number' &&
        typeof value.isoYear === 'number' &&
        typeof value.interimReport === 'boolean' &&
        isNumberList(value.locationIds)
    );
}

/** The week list, checked before a caller reads `weeks`. */
export function isLiceReportWeeksBody(value: unknown): value is LiceReportWeeksResponseDto {
    return isRecord(value) && Array.isArray(value.weeks) && value.weeks.every(isLiceReportWeek);
}

function isReportLocation(value: unknown): boolean {
    return (
        isRecord(value) &&
        typeof value.id === 'number' &&
        typeof value.name === 'string' &&
        Array.isArray(value.cages) &&
        value.cages.every(isRecord)
    );
}

function isWeeklyReport(value: unknown): value is AllWeeklyReportResult {
    return (
        isRecord(value) &&
        typeof value.isoWeek === 'number' &&
        typeof value.isoYear === 'number' &&
        typeof value.interimReport === 'boolean' &&
        Array.isArray(value.locations) &&
        value.locations.every(isReportLocation)
    );
}

function isParity(value: unknown): value is LiceParityDto {
    return (
        isRecord(value) &&
        Array.isArray(value.entries) &&
        typeof value.equal === 'boolean' &&
        typeof value.scope === 'string' &&
        isLiceSource(value.source) &&
        isRecord(value.summary)
    );
}

/** One weekly report, checked before the mapper reads `report`. */
export function isWeeklyLiceReportBody(value: unknown): value is WeeklyLiceReportResponseDto {
    if (!isRecord(value) || !isWeeklyReport(value.report) || !isLiceSource(value.source)) {
        return false;
    }
    return value.parity === undefined || isParity(value.parity);
}

export async function fetchLiceReportWeeks(
    clientId: number,
    configOverride?: AxiosRequestConfig
): Promise<SwaggerResponse<LiceReportWeeksResponseDto>> {
    const response = await Http.getRequest(
        WEEKS_URL(clientId),
        undefined,
        undefined,
        undefined,
        configOverride
    );
    const body: unknown = response.data;
    if (!isLiceReportWeeksBody(body)) {
        throw new Error('Unexpected lice report weeks response');
    }
    return { ...response, data: body };
}

export async function fetchWeeklyLiceReport(
    clientId: number,
    query: WeeklyLiceReportQuery,
    configOverride?: AxiosRequestConfig
): Promise<SwaggerResponse<WeeklyLiceReportResponseDto>> {
    const queryParams = {
        isoWeek: query.isoWeek,
        isoYear: query.isoYear,
        source: query.source,
        ...(query.locationId !== undefined ? { locationId: query.locationId } : {}),
    };

    const response = await Http.getRequest(
        WEEK_URL(clientId),
        queryParams,
        undefined,
        undefined,
        configOverride
    );
    const body: unknown = response.data;
    if (!isWeeklyLiceReportBody(body)) {
        throw new Error('Unexpected weekly lice report response');
    }
    return { ...response, data: body };
}
